import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { message } from '@client/components/_ui/feedback/message';
import { PageLoading } from '@client/components/_ui/feedback/PageLoading';
import { AuthLoadingScreen } from '@client/components/auth/AuthLoadingScreen';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useAuthStore } from '@client/stores/AuthStore';
import { isTrialExpiredError } from '@client/trpc/error-classification';
import { RouteChunkStallError } from '@client/utils/routing/route-chunk-stall';
import { SYSTEM_ORG_IDS } from '@shared/constants';
import { createFileRoute, type ErrorComponentProps, useMatch } from '@tanstack/react-router';
import { useEffect } from 'react';

export const Route = createFileRoute('/(authentication)/change-org/$orgId')({
    params: {
        parse({ orgId }) {
            return { orgId: BigInt(orgId) };
        },
    },
    errorComponent: ErrorComponent,
    component: ChangeOrg,
});

function ErrorComponent({ error }: ErrorComponentProps) {
    const navigate = useNavigate();
    // This route's code stopped arriving: pass it on to `AppProvider`'s boundary, which reports it
    // and offers the page reload that requests the code again.
    if (error instanceof RouteChunkStallError) {
        throw error;
    }
    return (
        <PageLoading>
            <div className="w-full px-4 py-12 md:py-0">
                <div className="mx-auto w-full max-w-md rounded-lg border border-separator bg-card p-6 text-center sm:p-10">
                    {/* curium:allow-heading — organisation switch error screen, outside the app shell */}
                    <h2 className="mb-2 font-bold text-2xl text-danger">Error</h2>
                    <p className="mb-6 text-base text-muted-foreground">{error?.toString()}</p>
                    <Button type="link" onClick={() => navigate({ moveInHistory: 'back' })} className="mx-auto">
                        Go back
                    </Button>
                </div>
            </div>
        </PageLoading>
    );
}

function ChangeOrg() {
    const match = useMatch({ strict: false });
    const { orgId } = Route.useParams();
    const user = useUser();
    const authStore = useAuthStore();
    const navigate = useNavigate();

    useEffect(() => {
        if (match?.paramsError) {
            return;
        }
        const handleChangeOrg = async () => {
            try {
                // Check if current org is already the requested org with a valid token
                const currentOrg = authStore.org;
                if (currentOrg && currentOrg.id === orgId && authStore.hasValidToken()) {
                    // Already in the correct org with valid token, just navigate
                    if (orgId === SYSTEM_ORG_IDS.SYSTEM_CONSOLE) {
                        navigate({ to: '/admin-console' });
                    } else {
                        navigate({ to: '/console/$orgId', params: { orgId } });
                    }
                    return;
                }

                // changeOrg handles queryClient.clear() and the pendingOrgId header transition.
                // No Auth0 round-trip — the existing token works for any org the user has access to.
                await authStore.changeOrg(orgId);

                if (orgId === SYSTEM_ORG_IDS.SYSTEM_CONSOLE) {
                    navigate({ to: '/admin-console' });
                } else {
                    navigate({ to: '/console/$orgId', params: { orgId } });
                }
            } catch (e) {
                // The target org's Light trial has ended: show the expiry screen for it. The store
                // still holds the current org, so signing in again would not help.
                if (isTrialExpiredError(e)) {
                    navigate({ to: '/trial-expired', search: { orgId }, replace: true });
                    return;
                }
                message.error('Error changing organisation. Redirecting to the login page...');
                console.error('Error changing organisation', e);
                navigate({ to: '/login', search: { orgId } });
            }
        };
        handleChangeOrg();
    }, [orgId]);

    if (match?.paramsError) {
        return null;
    }
    const org = user.userOrgs?.find((org) => org.id === orgId);

    if (!orgId || !org) {
        return (
            <PageLoading>
                <div className="w-full px-4 py-12 md:py-0">
                    <div className="mx-auto w-full max-w-md rounded-lg border border-separator bg-card p-6 text-center sm:p-10">
                        {/* curium:allow-heading — organisation switch error screen, outside the app shell */}
                        <h2 className="mb-2 font-bold text-2xl text-danger">Organisation not found</h2>
                        <p className="text-base text-muted-foreground">
                            The organisation you are trying to access does not exist or you do not have permission to
                            access it.
                        </p>
                    </div>
                </div>
            </PageLoading>
        );
    }

    return (
        <PageController route={Route}>
            <AuthLoadingScreen
                title={
                    orgId === SYSTEM_ORG_IDS.SYSTEM_CONSOLE
                        ? 'Signing in to the system console'
                        : `Signing in to ${org.name}`
                }
                hint="Preparing your workspace"
            />
        </PageController>
    );
}
